// ─── Onboarding Wizard — smart flow for any seller type ────────────────────
import { useState } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS, SPRING } from "../lib/theme.js";
import { supabase } from "../hooks/useData.js";

const TOTAL_STEPS = 6; // 0-5

function Progress({ current, total, T }) {
  return (
    <div style={{ display:"flex", gap:6, marginBottom:24 }}>
      {Array.from({ length:total }, (_, i) => (
        <div key={i} style={{
          flex:1, height:4, borderRadius:4,
          background:i < current ? "linear-gradient(90deg,#8b5cf6,#06b6d4)" : T.surfaceHi || T.border,
          transition:"background 0.3s ease",
          boxShadow:i < current ? "0 0 8px rgba(139,92,246,0.3)" : "none",
        }}/>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type="text", prefix, T }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <div style={{ fontSize:11, fontWeight:700, color:T.textSub, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 }}>{label}</div>}
      <div style={{ display:"flex", alignItems:"center", border:`1.5px solid ${T.border}`, borderRadius:RADIUS.md, background:T.surface, overflow:"hidden" }}>
        {prefix && <span style={{ padding:"0 12px", color:T.textSub, fontSize:18, fontWeight:700, borderRight:`1px solid ${T.border}` }}>{prefix}</span>}
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{ flex:1, padding:"12px 14px", border:"none", background:"transparent", color:T.text, fontSize:15, fontFamily:FONT, outline:"none", ...(prefix?{paddingLeft:12}:{}) }}/>
      </div>
    </div>
  );
}

function PrimaryBtn({ onClick, disabled, children, T }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width:"100%", padding:"13px",
      background:disabled ? T.surfaceHi || T.border : "linear-gradient(135deg,#8b5cf6,#06b6d4)",
      color:disabled ? T.textMuted : "#fff", border:"none",
      borderRadius:RADIUS.md, fontSize:15, fontWeight:700, fontFamily:FONT,
      cursor:disabled ? "not-allowed" : "pointer",
      boxShadow:disabled ? "none" : "0 8px 24px rgba(139,92,246,0.35)",
    }}>{children}</button>
  );
}

function SkipBtn({ onClick, T }) {
  return (
    <button onClick={onClick} style={{
      background:"transparent", border:"none", color:T.textSub,
      fontSize:12, fontFamily:FONT, cursor:"pointer", marginTop:14, padding:0,
      opacity:0.7, display:"block", width:"100%", textAlign:"center",
    }}>Skip this step →</button>
  );
}

function Tip({ children, T }) {
  return (
    <div style={{ fontSize:12, color:T.textMuted||T.textSub, lineHeight:1.6, marginBottom:16, padding:"10px 14px", background:T.surface, borderRadius:RADIUS.md, border:`1px solid ${T.border}` }}>
      {children}
    </div>
  );
}

// ── CSS-only animated robot ──
function AnimatedRobot({ T }) {
  return (
    <>
      <style>{`
        @keyframes robotBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        @keyframes robotBlink{0%,42%,46%,100%{transform:scaleY(1)}44%{transform:scaleY(0.1)}}
        @keyframes robotWave{0%,100%{transform:rotate(0deg)}25%{transform:rotate(15deg)}75%{transform:rotate(-15deg)}}
        @keyframes robotGlow{0%,100%{box-shadow:0 0 12px rgba(139,92,246,0.3)}50%{box-shadow:0 0 24px rgba(6,182,212,0.5)}}
      `}</style>
      <div style={{ animation:"robotBounce 3s ease-in-out infinite", display:"inline-block", marginBottom:16 }}>
        {/* Head */}
        <div style={{
          width:72, height:60, borderRadius:"18px 18px 14px 14px", margin:"0 auto",
          background:"linear-gradient(135deg,#8b5cf6,#7c3aed)",
          boxShadow:"0 8px 24px rgba(139,92,246,0.4)",
          position:"relative", animation:"robotGlow 4s ease-in-out infinite",
        }}>
          {/* Antenna */}
          <div style={{ position:"absolute", top:-14, left:"50%", transform:"translateX(-50%)", animation:"robotWave 2s ease-in-out infinite" }}>
            <div style={{ width:3, height:12, background:"#06b6d4", borderRadius:2, margin:"0 auto" }}/>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#06b6d4", margin:"-2px auto 0", boxShadow:"0 0 8px rgba(6,182,212,0.6)" }}/>
          </div>
          {/* Eyes */}
          <div style={{ display:"flex", justifyContent:"center", gap:14, paddingTop:16 }}>
            <div style={{ width:12, height:14, borderRadius:4, background:"#fff", animation:"robotBlink 4s ease infinite", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"#1a1a2e" }}/>
            </div>
            <div style={{ width:12, height:14, borderRadius:4, background:"#fff", animation:"robotBlink 4s ease 0.1s infinite", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"#1a1a2e" }}/>
            </div>
          </div>
          {/* Mouth */}
          <div style={{ width:20, height:6, borderRadius:"0 0 10px 10px", background:"#06b6d4", margin:"6px auto 0" }}/>
        </div>
        {/* Body */}
        <div style={{ width:52, height:28, borderRadius:"0 0 12px 12px", margin:"2px auto 0",
          background:"linear-gradient(180deg,#7c3aed,#6d28d9)", position:"relative" }}>
          <div style={{ position:"absolute", top:6, left:8, width:8, height:8, borderRadius:"50%", background:"#06b6d4", opacity:0.7 }}/>
          <div style={{ position:"absolute", top:6, right:8, width:8, height:8, borderRadius:"50%", background:"#06b6d4", opacity:0.7 }}/>
          <div style={{ position:"absolute", bottom:4, left:"50%", transform:"translateX(-50%)", width:16, height:4, borderRadius:2, background:"#06b6d4", opacity:0.4 }}/>
        </div>
      </div>
    </>
  );
}

export default function OnboardingWizard({ session, onComplete, setAvailableToday, setCurrency, currency }) {
  const T = useTheme();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [step, setStep] = useState(0);
  const [cfg, setCfg] = useState({
    useApi: null,       // true/false/null
    hasExtraStock: null, // true/false/null (only if useApi)
    initialUnits: "",
    costPerUnit: "",
    currency: currency || "USD",
    availableToday: "",
  });

  function next() { setStep(s => Math.min(s+1, TOTAL_STEPS-1)); }

  async function markOnboarded() {
    try { localStorage.setItem("sf_onboarded","true"); } catch {}
    try {
      const token = session?.access_token;
      if (token) {
        await fetch("/api/profile", {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ onboarding_completed: true }),
        });
      }
    } catch {}
  }

  async function finish() {
    try {
      if (cfg.availableToday && setAvailableToday) setAvailableToday(parseFloat(cfg.availableToday)||0);
      if (setCurrency && cfg.currency) setCurrency(cfg.currency);
    } catch (e) { console.error(e); }
    await markOnboarded();
    onComplete();
  }

  const sym = cfg.currency === "USD" ? "$" : cfg.currency === "GBP" ? "£" : "€";

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:10000,
      background:T.bg, fontFamily:FONT,
      display:"flex", alignItems:"flex-start", justifyContent:"center",
      paddingTop:"6vh", paddingBottom:"4vh", overflowY:"auto",
    }}>
      <style>{`
        @keyframes obFadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes obBubbleIn{from{opacity:0;transform:translateY(8px) scale(0.95)}to{opacity:1;transform:translateY(0) scale(1)}}
      `}</style>
      {/* Background glow */}
      <div style={{ position:"absolute", top:"20%", left:"50%", transform:"translateX(-50%)",
        width:"60vw", height:"50vh", borderRadius:"50%",
        background:"radial-gradient(ellipse, rgba(139,92,246,0.1) 0%, transparent 70%)",
        pointerEvents:"none", filter:"blur(80px)",
      }}/>

      <div style={{
        background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.xl,
        padding:isMobile?"20px 16px":"32px 28px", width:"100%", maxWidth:500,
        boxShadow:T.shadowLg || "0 20px 60px rgba(0,0,0,0.4)", position:"relative", zIndex:1,
        animation:"obFadeIn 0.35s ease both",
      }}>
        {step > 0 && step < TOTAL_STEPS-1 && <Progress current={step} total={TOTAL_STEPS-2} T={T}/>}

        {/* ── Step 0: Welcome ── */}
        {step === 0 && (
          <div style={{ textAlign:"center", padding:"10px 0", animation:"obFadeIn 0.3s ease both" }}>
            <div style={{
              width:64, height:64, borderRadius:18, margin:"0 auto 18px",
              background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
              boxShadow:"0 12px 40px rgba(139,92,246,0.4)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:11, fontWeight:800, color:"#fff",
            }}>CH</div>
            <div style={{ fontSize:26, fontWeight:800, color:T.text, letterSpacing:-0.5, marginBottom:8 }}>Welcome to CashHorizon</div>
            <div style={{ fontSize:14, color:T.textSub, lineHeight:1.6, marginBottom:28, maxWidth:380, margin:"0 auto 28px" }}>
              Let's get your cashflow forecast running in under a minute.
            </div>
            <PrimaryBtn onClick={next} T={T}>Get Started →</PrimaryBtn>
          </div>
        )}

        {/* ── Step 1: API or Manual ── */}
        {step === 1 && (
          <div style={{ animation:"obFadeIn 0.25s ease both" }}>
            <div style={{ fontSize:20, fontWeight:800, color:T.text, marginBottom:6, letterSpacing:-0.3 }}>How do you want to get started?</div>
            <div style={{ fontSize:13, color:T.textSub, marginBottom:20, lineHeight:1.5 }}>This helps us set up the right experience for you.</div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
              {[
                { val:true, icon:"🔗", title:"I'll connect my marketplace API", desc:"Amazon, Shopify, eBay, or Walmart. We'll pull your products, sales, and inventory automatically." },
                { val:false, icon:"✏️", title:"I'll enter data manually", desc:"No API needed. You'll add your products and numbers by hand." },
              ].map(opt => {
                const sel = cfg.useApi === opt.val;
                return (
                  <button key={String(opt.val)} onClick={()=>{ setCfg(c=>({...c, useApi:opt.val})); next(); }}
                    style={{
                      display:"flex", gap:14, alignItems:"flex-start", textAlign:"left",
                      padding:"16px 18px", borderRadius:RADIUS.md, cursor:"pointer",
                      background:sel ? T.accent+"12" : T.surface,
                      border:`2px solid ${sel ? T.accent : T.border}`,
                      transition:`all 0.15s ${SPRING}`,
                    }}>
                    <span style={{ fontSize:28, flexShrink:0, marginTop:2 }}>{opt.icon}</span>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:3 }}>{opt.title}</div>
                      <div style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <SkipBtn onClick={next} T={T}/>
          </div>
        )}

        {/* ── Step 2: Inventory ── */}
        {step === 2 && (
          <div style={{ animation:"obFadeIn 0.25s ease both" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <span style={{ fontSize:22 }}>📦</span>
              <span style={{ fontSize:20, fontWeight:800, color:T.text, letterSpacing:-0.3 }}>Your inventory</span>
            </div>

            <div style={{ fontSize:13, color:T.textSub, lineHeight:1.8, marginBottom:18 }}>
              Your inventory is tracked automatically through three sources:
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:18 }}>
              <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px 14px", background:T.surface, borderRadius:RADIUS.md, border:`1px solid ${T.border}` }}>
                <span style={{ fontSize:18, flexShrink:0 }}>🔌</span>
                <div><div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:2 }}>Marketplace API sync</div><div style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>Connect Amazon, Shopify, eBay, or Walmart on the Marketplaces page. Stock levels sync automatically and create an inventory pool.</div></div>
              </div>
              <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px 14px", background:T.surface, borderRadius:RADIUS.md, border:`1px solid ${T.border}` }}>
                <span style={{ fontSize:18, flexShrink:0 }}>📦</span>
                <div><div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:2 }}>Inventory Pools</div><div style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>Group marketplaces that share physical stock (e.g. FBA warehouse serves both Amazon and Shopify). Set stock manually on the Marketplaces page under Inventory Pools.</div></div>
              </div>
              <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px 14px", background:T.surface, borderRadius:RADIUS.md, border:`1px solid ${T.border}` }}>
                <span style={{ fontSize:18, flexShrink:0 }}>🚢</span>
                <div><div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:2 }}>Supplier Orders</div><div style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>Incoming stock from your suppliers. Add orders with arrival dates and the stock projection will include them automatically.</div></div>
              </div>
            </div>
            <PrimaryBtn onClick={next} T={T}>Continue →</PrimaryBtn>
            <Tip T={T}>💡 You can set all of this up after the wizard — head to Marketplaces or Supplier Orders anytime.</Tip>
          </div>
        )}

        {/* ── Step 3: Bank balance + Currency ── */}
        {step === 3 && (
          <div style={{ animation:"obFadeIn 0.25s ease both" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <span style={{ fontSize:22 }}>💰</span>
              <span style={{ fontSize:20, fontWeight:800, color:T.text, letterSpacing:-0.3 }}>Cash position</span>
            </div>
            <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6, marginBottom:14 }}>
              What's your current balance across all bank accounts?
            </div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.textSub, letterSpacing:0.5, textTransform:"uppercase", marginBottom:8 }}>Currency</div>
              <div style={{ display:"flex", gap:6 }}>
                {[["USD","$ Dollar"],["EUR","€ Euro"],["GBP","£ Pound"]].map(([val,label])=>(
                  <button key={val} onClick={()=>{ setCfg(c=>({...c,currency:val})); if(setCurrency) setCurrency(val); }} style={{
                    flex:1, padding:"9px 12px", borderRadius:10, cursor:"pointer",
                    background:cfg.currency===val ? T.accent+"18" : "transparent",
                    border:`1.5px solid ${cfg.currency===val ? T.accent : T.border}`,
                    color:cfg.currency===val ? T.accent : T.textSub,
                    fontWeight:cfg.currency===val ? 700 : 400,
                    fontSize:13, fontFamily:FONT, transition:"all 0.15s",
                  }}>{label}</button>
                ))}
              </div>
            </div>
            <Field T={T} label="Total balance right now" value={cfg.availableToday} onChange={v=>setCfg(c=>({...c,availableToday:v}))} placeholder="10000" prefix={sym} type="number"/>
            <Tip T={T}>💡 This is your starting point. The app adds income and subtracts expenses from here — you only enter it once.</Tip>
            <PrimaryBtn onClick={next} disabled={!cfg.availableToday} T={T}>Continue →</PrimaryBtn>
            <SkipBtn onClick={next} T={T}/>
          </div>
        )}

        {/* ── Step 4: Getting started guide ── */}
        {step === 4 && (
          <div style={{ animation:"obFadeIn 0.25s ease both" }}>
            <div style={{ fontSize:20, fontWeight:800, color:T.text, marginBottom:6, letterSpacing:-0.3 }}>Here's how to get started</div>
            <div style={{ fontSize:13, color:T.textSub, marginBottom:18, lineHeight:1.5 }}>Follow these steps and your forecast will be accurate in minutes.</div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
              {[
                { n:1, icon:"🛒", title:"Set up your marketplaces", desc:cfg.useApi
                  ? "Go to Marketplaces and connect your API. We'll pull products and payouts automatically."
                  : "Go to Marketplaces and add your products with their payout per unit. Tip: you can use a single product with a blended payout per marketplace — no need to list every SKU." },
                { n:2, icon:"🎯", title:"Set your sales targets", desc:"Open Targets and enter how many units you plan to sell each month. This drives your income forecast." },
                { n:3, icon:"📦", title:"Register existing orders", desc:"If you have supplier orders in progress, add them in Supplier Orders with payment terms and arrival dates." },
                { n:4, icon:"📅", title:"Add recurring costs", desc:"Monthly rent, software, ads? Add them as recurring expenses so they appear on your Cash Calendar." },
                { n:5, icon:"📌", title:"Plan big decisions", desc:"Use the Planner to log upcoming decisions (new product launch, hiring, reorders). Each decision can have a financial impact — the app calculates how it affects your cashflow." },
                { n:6, icon:"📊", title:"Check your Dashboard", desc:"Everything flows into the Dashboard. Watch your balance, runway, and health score update in real-time." },
              ].map(s => (
                <div key={s.n} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"12px 14px", background:T.surface, borderRadius:RADIUS.md, border:`1px solid ${T.border}` }}>
                  <div style={{
                    width:28, height:28, borderRadius:8, flexShrink:0,
                    background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:13, fontWeight:800, color:"#fff",
                  }}>{s.n}</div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{s.icon} {s.title}</div>
                    <div style={{ fontSize:11, color:T.textSub, lineHeight:1.5, marginTop:2 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <PrimaryBtn onClick={next} T={T}>Next →</PrimaryBtn>
          </div>
        )}

        {/* ── Step 5: AI showcase ── */}
        {step === 5 && (
          <div style={{ animation:"obFadeIn 0.25s ease both" }}>
            <div style={{ textAlign:"center", marginBottom:12 }}>
              <AnimatedRobot T={T}/>
              <div style={{ fontSize:20, fontWeight:800, color:T.text, letterSpacing:-0.3, marginBottom:4 }}>Your AI assistant can do all of this</div>
              <div style={{ fontSize:12, color:T.textSub, marginBottom:16 }}>Via chat, WhatsApp, or Telegram — no screen needed</div>
            </div>
            <div style={{ maxHeight:300, overflowY:"auto", display:"flex", flexDirection:"column", gap:5, marginBottom:16, paddingRight:4 }}>
              {[
                { type:"user", icon:"💬", text:"What's my cashflow looking like for the next 3 months?" },
                { type:"robot", reaction:"📊", text:"On it!" },
                { type:"user", icon:"📦", text:"Add a $15,000 order from my supplier, 2000 units, arriving June 15" },
                { type:"robot", reaction:"👍", text:"Order created!" },
                { type:"user", icon:"✅", text:"Mark my rent as paid" },
                { type:"user", icon:"🔮", text:"What if my sales drop 20% next quarter?" },
                { type:"robot", reaction:"🤔", text:"Let me simulate..." },
                { type:"user", icon:"🛒", text:"Create a new product on Amazon: Wireless Earbuds, $28 payout" },
                { type:"robot", reaction:"⚡", text:"Done!" },
                { type:"user", icon:"📉", text:"How many days until I run out of stock?" },
                { type:"user", icon:"🔄", text:"Add a recurring expense: $500/month for warehouse storage" },
                { type:"robot", reaction:"🔄", text:"Added!" },
                { type:"user", icon:"⏰", text:"Delay next week's supplier payment to the end of the month" },
                { type:"user", icon:"🚨", text:"Show me all overdue payments" },
                { type:"robot", reaction:"🚨", text:"Found 3!" },
                { type:"user", icon:"✏️", text:"Change my Shopify delivery cost to $3.50 per unit" },
                { type:"robot", reaction:"✨", text:"Updated!" },
              ].map((ex,i) => (
                ex.type === "robot" ? (
                  <div key={i} style={{
                    display:"flex", gap:6, alignItems:"center", justifyContent:"flex-end",
                    animation:`obBubbleIn 0.25s ease both ${0.1 + i * 0.06}s`,
                  }}>
                    <div style={{
                      background:"rgba(6,182,212,0.1)", border:"1px solid rgba(6,182,212,0.2)",
                      borderRadius:"12px 12px 4px 12px", padding:"5px 10px",
                      fontSize:11, color:"#06b6d4", fontWeight:600,
                      display:"flex", alignItems:"center", gap:5,
                    }}>
                      <span style={{ fontSize:13 }}>{ex.reaction}</span> {ex.text}
                    </div>
                    <div style={{
                      width:22, height:22, borderRadius:6, flexShrink:0,
                      background:"linear-gradient(135deg,#8b5cf6,#7c3aed)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:10,
                    }}>🤖</div>
                  </div>
                ) : (
                  <div key={i} style={{
                    display:"flex", gap:8, alignItems:"flex-start",
                    animation:`obBubbleIn 0.3s ease both ${0.1 + i * 0.06}s`,
                  }}>
                    <span style={{ fontSize:14, flexShrink:0, marginTop:2 }}>{ex.icon}</span>
                    <div style={{
                      background:"rgba(139,92,246,0.1)", border:"1px solid rgba(139,92,246,0.2)",
                      borderRadius:"12px 12px 12px 4px", padding:"8px 12px",
                      fontSize:12, color:T.text, lineHeight:1.5, fontWeight:500,
                    }}>"{ex.text}"</div>
                  </div>
                )
              ))}
            </div>
            <PrimaryBtn onClick={finish} T={T}>Go to Dashboard →</PrimaryBtn>
          </div>
        )}
      </div>
    </div>
  );
}
